import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface BorderedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  className?: string;
  textClassName?: string;
  useTooltip?: boolean;
  formatDisplay?: (value: string) => string; // Function to format the display value
  parseValue?: (value: string) => string; // Function to parse the formatted value back to raw value
}

const BorderedInput: React.FC<BorderedInputProps> = ({
  label,
  error,
  className,
  textClassName,
  formatDisplay,
  parseValue,
  value,
  onChange,
  onFocus,
  onBlur,
  useTooltip = false,
  ...inputProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update display value when value prop changes
  useEffect(() => {
    if (!isFocused) {
      const stringValue = value?.toString() || '';
      if (formatDisplay && stringValue) {
        setDisplayValue(formatDisplay(stringValue));
      } else {
        setDisplayValue(stringValue);
      }
    }
  }, [value, formatDisplay, isFocused]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // Show raw value when focused
    const stringValue = value?.toString() || '';
    setDisplayValue(stringValue);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Format value when blurred
    const stringValue = value?.toString() || '';
    if (formatDisplay && stringValue) {
      setDisplayValue(formatDisplay(stringValue));
    } else {
      setDisplayValue(stringValue);
    }
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = e.target.value;

    // If parseValue is provided, use it to clean the input
    if (parseValue) {
      rawValue = parseValue(rawValue);
    }

    // Update display to show what user is typing
    setDisplayValue(rawValue);

    // Create synthetic event with the raw value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: rawValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  };

  const rawValue = value?.toString() || '';
  const shouldShowTooltip = useTooltip && rawValue && !isFocused && displayValue.length > 22;

  const inputWrapper = (
    <div
      className={`group relative rounded ${error ? 'border-red-500' : ''} ${className || ''}`}
      style={{
        paddingLeft: '1px',
        paddingRight: '5px',
        display: 'inline-block',
        maxWidth: '100%',
        maxHeight: 36,
      }}
    >
      {label && (
        <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500 z-10 pointer-events-none">
          {label}
        </label>
      )}
      <input
        ref={inputRef}
        className={`
          appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
          border-1 border-transparent 
          group-hover:border-gray-500 
          group-hover:border 
          focus:outline-none
          focus:border-blue-500
          ${inputProps.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
          ${textClassName || 'text-gray-500'}
          ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500 border border-red-500' : ''}
        `}
        style={{
          maxHeight: 36,
        }}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...inputProps}
      />
    </div>
  );

  return (
    <div className="flex flex-col">
      {shouldShowTooltip ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              {inputWrapper}
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="whitespace-nowrap max-w-[700px]"
            >
              {displayValue}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        inputWrapper
      )}
      {error && (
        <span className="mt-1 text-xs text-red-500">
          {error}
        </span>
      )}
    </div>
  );
};

export default BorderedInput;